/**
 * zh-CN 简体字典——键树必须与 zh-HK 基线完全一致（i18n:check 把关）。
 * 用词按大陆习惯校订（支持/视频/邮箱/服务器/标签/信息）。
 */
import type { Dict } from '../index';

const dict: Dict = {
  // Site meta
  'meta.title': 'JKD Coding｜以无法为有法 — AI 商业顾问',
  'meta.description': 'JKD Coding 融合李小龙截拳道哲学，为 NGO、社会企业与中小企业提供 AI 转化诊断、增长系统设计与客户关系运营。30 分钟诊断，找出 AI 最自然的介入点。',

  // Navbar
  'nav.arsenal': '现成方案',
  'nav.components': '组件库',
  'nav.custom': '全定制项目',
  'nav.philosophy': '方法论',
  'nav.consult': '免费咨询',

  // Hero
  'hero.kicker': 'Be Water, My Friend',
  'hero.titleA': '如水之智',
  'hero.titleB': '快狠准解决商业问题',
  'hero.subtitle': 'JKD Coding 为 NGO、社会企业与中小企业设计 AI 转化系统：不强加技术，只为你的业务找到最自然的形态，让捐赠增长、客户保留与现金流预警一次到位。',
  'hero.cta': '预约 AI 转化诊断',
  'hero.approach': '了解我们的方法',
  'hero.quote': '清空你的脑袋，无形无状，如水一般。',

  // Services
  'services.tag': '我们的服务领域',
  'services.title': '不是卖工具，是解决问题',
  'services.subtitle': '每个项目都从你的商业瓶颈出发，再决定 AI 该以什么形态介入。',
  'services.m1.eyebrow': '01 / 增长',
  'services.m1.title': '捐赠者与客户增长',
  'services.m1.problem': '潜在支持者来了却不转化，获客成本越来越高。',
  'services.m1.solution': '设计 AI 驱动的转化漏斗，把每一次互动变成长期关系的起点。',
  'services.m1.result': '提升转化率，降低获客成本',
  'services.m2.eyebrow': '02 / 关系',
  'services.m2.title': '客户关系运营',
  'services.m2.problem': '跟进遗漏、回复太慢、客户渐渐冷却。',
  'services.m2.solution': '把一对一沟通变成可预测、可规模的关系运营系统。',
  'services.m2.result': '减少流失，提升回头率',
  'services.m3.eyebrow': '03 / 风险',
  'services.m3.title': '现金流与风险预警',
  'services.m3.problem': '坏账发生后才发现，客户转投对手才察觉。',
  'services.m3.solution': 'AI 每日监察行为信号，在风险发生前主动提醒你。',
  'services.m3.result': '保护现金流，提前应对风险',
  'services.m4.eyebrow': '04 / 采用',
  'services.m4.title': '变革采用与运营效率',
  'services.m4.problem': '系统上线，员工却不愿意用，培训成本高。',
  'services.m4.solution': '从习惯出发设计采用路径，让改变自然发生。',
  'services.m4.result': '提升采用率，减少操作错误',
  'services.m5.eyebrow': '05 / 知识',
  'services.m5.problem': '会议开完，经验就跟着蒸发。',
  'services.problemLabel': '常见问题',
  'services.solutionLabel': '我们的做法',
  'services.cta': '预约一次无压力的业务诊断',

  // Arsenal
  'arsenal.tag': 'READY-TO-DEPLOY',
  'arsenal.title': '现成方案',
  'arsenal.subtitle': '场景化解决方案封装，快速部署，即战即用',
  'arsenal.blindbox.title': '盲盒系统',
  'arsenal.blindbox.subtitle': '季节性行销神器',
  'arsenal.blindbox.desc': '概率配置引擎、奖品库管理、社交扩散机制、数据追踪面板。适合 NGO 筹款、电商促销、KOL 互动。',
  'arsenal.blindbox.f1': '季节版本主题（CNY/圣诞/中秋）',
  'arsenal.blindbox.f2': '自定义概率与保底机制',
  'arsenal.blindbox.f3': '社交分享解锁功能',
  'arsenal.blindbox.f4': '完整数据分析面板',
  'arsenal.blindbox.f5': '30 分钟视频支持',
  'arsenal.whatsapp.title': 'WhatsApp 客制化自动跟进系统',
  'arsenal.whatsapp.subtitle': '客户关系引擎',
  'arsenal.whatsapp.desc': '灵活预配置工作流，支持弃置购物车、订单更新、预约确认、满意度调查。',
  'arsenal.whatsapp.f1': '预设消息模板库',
  'arsenal.whatsapp.f2': '聊天机器人配置',
  'arsenal.whatsapp.f3': 'CRM 系统整合',
  'arsenal.whatsapp.f4': '30 分钟内部署完成',
  'arsenal.whatsapp.f5': '持续技术支持',
  'arsenal.promise': '10 分钟 拥有你的转化漏斗',
  'arsenal.priceFrom': '起',

  // Products
  'products.tag': 'NEW RELEASES',
  'products.title': 'AI 业务工具',
  'products.subtitle': '专为中小企业设计的 AI 业务助手，立即体验互动式演示。',
  'products.cta': '立即体验',
  'products.landlink.title': 'Landlink',
  'products.landlink.subtitle': 'AI 习惯落地助手',
  'products.landlink.desc': '专门解决"系统上线，员工/客户却不肯用"的问题。AI 读取旧有操作习惯，主动准备最常用的工作，把繁琐流程浓缩成一步。',
  'products.landlink.f1': '读取旧习惯，预先准备下一步',
  'products.landlink.f2': '管理层采用进度摘要 + 每日 WhatsApp 提醒',
  'products.landlink.f3': '前线个人积分与一键重复操作',
  'products.landlink.f4': '把五步变一步，降低培训与出错成本',
  'products.bizpulse.title': 'Bizpulse',
  'products.bizpulse.subtitle': 'AI 客户风险监察员',
  'products.bizpulse.desc': '24 小时监察客户行为，提早发现拖欠账款、客户流失、转投对手等风险。让你在坏账发生前主动出击，保住现金流。',
  'products.bizpulse.f1': 'AI 每日巡查客户纪录',
  'products.bizpulse.f2': '自动识别付款变慢与互动冷却',
  'products.bizpulse.f3': '即时风险预警通知',
  'products.bizpulse.f4': '无需学 AI，开箱即用',

  // Components
  'components.tag': 'COMPONENTS LIBRARY',
  'components.title': '组件库',
  'components.subtitle': '模块化能力开放，按需组合，技术自主',
  'components.quote': '"想都是问题，做才有答案"',
  'components.scroll.name': '滚动触发动画模块',
  'components.scroll.tech': 'Intersection Observer API',
  'components.scroll.desc': '支持淡入、滑入、缩放、旋转等 12 种动效，可配置触发阈值和延迟',
  'components.i18n.name': '双语 i18n 架构',
  'components.i18n.tech': 'URL 参数 / 浏览器侦测',
  'components.i18n.desc': '基于 URL 参数或浏览器语言自动侦测的内容切换系统，支持 RTL 语言预留',
  'components.cf-track.name': 'Cloudflare Workers 追踪',
  'components.cf-track.tech': 'Serverless',
  'components.cf-track.desc': '无服务器架构的用户行为追踪，支持自定义事件、用户分群、与 GA4 双轨并行',
  'components.cf-webhook.name': 'Cloudflare Workers Webhook',
  'components.cf-webhook.tech': 'HTTP Endpoints',
  'components.cf-webhook.desc': '标准化 HTTP 端点，支持请求验证、速率限制、与 Zapier/Make 整合',
  'components.og.name': 'OG 标签优化套件',
  'components.og.tech': 'Dynamic Meta Tags',
  'components.og.desc': '动态 Open Graph 标签生成，支持 A/B 测试和社交媒体预览模拟',
  'components.full.title': '组件库全库',
  'components.full.desc': '包含所有组件 + 优先支持通道 + 季度更新订阅，适合需要系统性技术能力的中大型项目',
  'components.full.tag1': '全部组件',
  'components.full.tag2': '优先支持',
  'components.full.tag3': '季度更新',
  'components.perYear': '/年',
  'components.orMonthly': '或 HK$200/月',

  // Custom
  'custom.tag': 'CUSTOM DOJO',
  'custom.title': '全定制项目',
  'custom.subtitle': '全定制转化漏斗，从零到一，打造独一无二的转化机器',
  'custom.price': '项目报价',
  'custom.priceNote': '根据项目范围与复杂度定制',
  'custom.ctaNote': '无需承诺，先了解你的业务需求',
  'custom.phase1.week': 'Week 1',
  'custom.phase1.title': '竞品研究与机会识别',
  'custom.phase1.desc': '系统性拆解 5–10 个顶尖竞品：热图逆向工程、性能评分、SEO 关键词重叠',
  'custom.phase1.deliverable': '转化差距分析报告',
  'custom.phase2.week': 'Week 1–2',
  'custom.phase2.title': '文案策略与信息架构',
  'custom.phase2.desc': 'Jobs-to-be-Done 用户访谈、主标题 10 变体、社会证明策略、CTA 动词优化',
  'custom.phase2.deliverable': '文案策略手册',
  'custom.phase3.week': 'Week 2–4',
  'custom.phase3.title': 'UI/UX 设计与原型验证',
  'custom.phase3.desc': '双轨冲刺模式、Figma 高保真可交互原型、真实设备测试',
  'custom.phase3.deliverable': '可点击原型 + 设计规范',
  'custom.phase4.week': 'Week 4–6',
  'custom.phase4.title': '技术开发与部署',
  'custom.phase4.desc': '用户界面（UI）视觉层构建、JSON 逻辑配置、Cloudflare Workers 边缘部署',
  'custom.phase4.deliverable': '生产环境网站',
  'custom.phase5.week': 'Week 6–8',
  'custom.phase5.title': '数据追踪与优化',
  'custom.phase5.desc': 'GA4 流量分析、Hotjar 行为热图、A/B 测试框架、自定义转化仪表板',
  'custom.phase5.deliverable': '数据监测报告',
  'custom.cta': '预约免费咨询',

  // Philosophy
  'philosophy.tag': 'PHILOSOPHY',
  'philosophy.title': '方法论',
  'philosophy.subtitle': 'Vibe-coding 方法论，授人以渔的高阶知识产品',
  'philosophy.pillar1.title': '截拳道哲学基础',
  'philosophy.pillar1.desc': '李小龙"以无法为有法"思想与现代敏捷开发的深度对话，探索武术智慧如何转化为数码开发心法',
  'philosophy.pillar2.title': 'RPE 方法论框架',
  'philosophy.pillar2.desc': 'Rapid Prototyping & Execution 快速原型与执行完整操作手册：需求访谈、原型工具选择、用户测试、迭代节奏',
  'philosophy.pillar3.title': '工具评测与实战',
  'philosophy.pillar3.desc': 'Bubble、Webflow、Framer 等零代码/低代码平台深度评测，不回避缺点，建立客观权威',

  // FAQ
  'faq.tag': 'FAQ',
  'faq.title': '常见问题',
  'faq.subtitle': '关于 JKD Coding 服务的最直接解答',
  'faq.q1': '你们为哪类机构服务？',
  'faq.a1': '我们专为 NGO、社会企业与中小企业服务。无论是捐赠者增长、客户关系运营，还是现金流风险预警，我们都从你的实际业务问题出发设计方案。',
  'faq.q2': 'AI 转化诊断是什么？会发生什么事？',
  'faq.a2': '这是一次 30 分钟的一对一顾问对谈。我们会厘清你目前最大的业务瓶颈，并找出 AI 最自然的介入点；对谈后你会收到具体的改善建议，过程无任何销售压力。',
  'faq.q3': '收费如何计算？',
  'faq.a3': '现成方案 HK$800 起（盲盒系统）、WhatsApp 自动跟进系统 HK$1,500 起；组件库单项 HK$300–800，全库订阅 HK$2,000/年；全定制项目按范围报价，一般为 HK$15,000–50,000。',
  'faq.q4': '多久可以上线？',
  'faq.a4': '现成方案最快 30 分钟完成部署；组件库按需组合，通常数天内交付；全定制项目按五个阶段推进，一般 6–8 周完成并部署上线。',
  'faq.q5': '我们不懂技术，可以合作吗？',
  'faq.a5': '完全可以。技术细节由我们处理，你只需要说出业务目标。所有查询在 24 小时内回复，方案确认后亦有持续技术支持。',
  'faq.author': '作者：JKD Coding 团队',
  'faq.updated': '更新于 2026 年 9 月',

  // Contact
  'contact.tag': 'GET IN TOUCH',
  'contact.title': '开始你的旅程',
  'contact.subtitle': '「人生……就像大便一样，有时软、有时硬；有时顺畅，有时不顺畅！」',
  'contact.form.title': '发送消息',
  'contact.form.name': '姓名',
  'contact.form.namePlaceholder': '你的姓名',
  'contact.form.email': '邮箱',
  'contact.form.emailPlaceholder': 'your@email.com',
  'contact.form.business': '业务类型',
  'contact.form.businessPlaceholder': '选择你的业务类型',
  'contact.form.businessNgo': 'NGO / 社会企业',
  'contact.form.businessSme': '中小企业',
  'contact.form.businessPersonal': '个人品牌',
  'contact.form.businessOther': '其他',
  'contact.form.message': '消息',
  'contact.form.messagePlaceholder': '简单描述你的需求或问题...',
  'contact.form.submit': '发送消息',
  'contact.form.processing': '处理中...',
  'contact.form.waNote': '下一步需要通过 WhatsApp 验证身份，请确保你能够使用 WhatsApp。',
  'contact.verify.title': '验证你的 WhatsApp',
  'contact.verify.desc': '请点击下方按钮打开 WhatsApp，发送验证消息以完成提交。',
  'contact.verify.codeLabel': '你的验证码 Your Verification Code',
  'contact.verify.openWa': '打开 WhatsApp 验证 / Open WhatsApp',
  'contact.verify.prefillNote': '验证消息将自动填入 Verification message will be pre-filled',
  'contact.verify.back': '← 返回修改资料 / Go Back',
  'contact.success.title': '验证成功！',
  'contact.success.desc': '我们已收到你的查询，会在 24 小时内回复你。',
  'contact.quick.title': '快速联络',
  'contact.quick.whatsapp': 'WhatsApp 咨询',
  'contact.quick.whatsappDesc': '即时回复，预约免费咨询',
  'contact.quick.response': '快速回应承诺',
  'contact.quick.responseDesc': '所有查询 24 小时内回复',
  'contact.promise': '「无论你是 NGO、社企还是中小企业，我们都会用最直接的方式，为你找到最合适的解决方案。无需担心技术细节，只要说出你的业务目标即可。」',
  'contact.promiseAuthor': '— JKD Coding 团队',
  'contact.alertFail': '提交失败，请重试或直接 WhatsApp 联络我们。',
  'contact.waPrefill': '你好！我想了解 JKD Coding 的服务。',

  // Footer
  'footer.desc': '融合李小龙截拳道哲学，以"无法为有法"作为核心创新思维，为 NGO、社企、中小企业打造转化漏斗。',
  'footer.products': '产品',
  'footer.content': '内容',
  'footer.about': '关于',
  'footer.aboutUs': '关于我们',
  'footer.contact': '联络',
  'footer.faq': '常见问题',
  'footer.privacy': '隐私政策',
  'footer.terms': '服务条款',
  'footer.rights': 'All rights reserved.',
  // Diagnostic share card（P0 裂變入口）
  'share.title': '推荐给一位朋友',
  'share.desc': '知道有企业正被系统采用问题困扰？把免费诊断分享给对方。',
  'share.copy': '复制链接',
  'share.copied': '已复制！',
  'share.whatsapp': '通过 WhatsApp 分享',
  'share.more': '更多分享方式',
  'share.prefill': '一位朋友推荐我试试 JKD Coding 的免费 AI 转化诊断（30 分钟，无销售压力）：',

  // Diagnostic page（/diagnosis）
  'diag.tag': '30 分钟策略对谈',
  'diag.title': 'AI 转化诊断',
  'diag.subtitle': '30 分钟，厘清你的业务瓶颈，并找出 AI 最自然的介入点。',
  'diag.back': '返回主页',
  'diag.step1Title': '先了解你的处境',
  'diag.step1Desc': '填写后，我们的顾问会在 1 个工作天内与你联络。',
  'diag.name': '姓名 *',
  'diag.organization': '机构名称 *',
  'diag.role': '职位 *',
  'diag.email': '邮箱 *',
  'diag.businessType': '业务类型 *',
  'diag.businessPlaceholder': '请选择',
  'diag.ngo': 'NGO / 社会企业',
  'diag.sme': '中小企业',
  'diag.personal': '个人品牌',
  'diag.other': '其他',
  'diag.challenge': '目前最大的转化挑战是什么？ *',
  'diag.challengePlaceholder': '请选择',
  'diag.challenge1': '潜在客户来了但不转化',
  'diag.challenge2': '客户跟进遗漏或回复太慢',
  'diag.challenge3': '坏账或客户流失风险难以预警',
  'diag.challenge4': '新系统上线后团队不愿使用',
  'diag.challenge5': '内容产能不足，难以持续沟通',
  'diag.goals': '希望优先解决的商业目标 *',
  'diag.goalsPlaceholder': '简单描述你希望达成的结果，例如：提升捐款完成率 30%',
  'diag.budget': '可接受的预算范围（可选）',
  'diag.budgetPlaceholder': '请选择',
  'diag.budget1': 'HK$8,000 – 20,000',
  'diag.budget2': 'HK$20,000 – 50,000',
  'diag.budget3': 'HK$50,000 – 150,000',
  'diag.budget4': 'HK$150,000+',
  'diag.privacy': '你的资料仅用于本次对谈安排，绝不转售。',
  'diag.submit': '预约我的专属诊断',
  'diag.submitting': '处理中...',
  'diag.verifyTitle': '验证你的 WhatsApp',
  'diag.verifyDesc': '请点击下方按钮打开 WhatsApp，发送验证消息以完成预约。',
  'diag.code': '你的验证码',
  'diag.openWhatsApp': '打开 WhatsApp 验证',
  'diag.prefilled': '验证消息将自动填入',
  'diag.goBack': '返回修改资料',
  'diag.successTitle': '验证成功！',
  'diag.successDesc': '我们已收到你的诊断申请，会在 24 小时内回复你。',
  'diag.alertFail': '提交失败，请重试。',
  'diag.msgOrg': '机构名称',
  'diag.msgRole': '职位',
  'diag.msgChallenge': '目前最大挑战',
  'diag.msgGoals': '希望优先解决的目标',
  'diag.msgBudget': '可接受预算范围',
  'diag.msgUnfilled': '未填写',
  // Landlink（/landlink）
  'landlink.brandName': '落地通',
  'landlink.backAria': '返回首页',
  'landlink.brandAria': '落地通 LandLink',
  'landlink.zero.kicker': 'AI 习惯落地助手',
  'landlink.zero.titleA': '系统上线了，',
  'landlink.zero.titleB': '员工／客户不肯用？',
  'landlink.zero.body': '落地通读取旧有操作，主动准备最常用的工作，',
  'landlink.zero.bodyStrong': '将五步变一步。',
  'landlink.zero.cta': '先看阻力在哪里',
  'landlink.zero.dashLabel': 'ADOPTION RATE',
  'landlink.one.kicker': 'AI 正在替你拆解采用阻力',
  'landlink.one.title': '技术已上线，习惯未必。',
  'landlink.one.body': '按下 3 个信号，看看员工或客户不用系统的真正原因。',
  'landlink.one.closeAria': '关闭',
  'landlink.one.continueNext': '继续查看下一个信号 →',
  'landlink.one.continueDone': '查看 AI 如何接手 →',
  'landlink.one.progressPre': '已查看',
  'landlink.one.progressPost': '个信号',
  'landlink.one.f1.eyebrow': '行为阻力：熟悉感',
  'landlink.one.f1.title': '旧方法，零思考成本',
  'landlink.one.f1.text': '纸笔已经变成本能；新系统就算只多一步，也会在最忙的时候被放弃。问题不在功能，而在转换习惯的成本。',
  'landlink.one.f2.eyebrow': '行为阻力：出错恐惧',
  'landlink.one.f2.title': '前线害怕按错',
  'landlink.one.f2.text': '当操作结果不够清晰，前线人员会选择最安全的旧路。需要的不是再一次培训，而是即时、确定的反馈。',
  'landlink.one.f3.eyebrow': '行为阻力：缺乏回报',
  'landlink.one.f3.title': '转变与我无关',
  'landlink.one.f3.text': '公司得到效率，前线却只感受到麻烦。把回报落到个人身上，采用才会由要求变成主动选择。',
  'landlink.two.kicker': 'AI Agent 不是概念・现在看他真正做事',
  'landlink.two.titlePre': '5 步变 ',
  'landlink.two.titleHighlight': '1 步',
  'landlink.two.titlePost': '。',
  'landlink.two.body': '同一张单，两种做法。落差就是员工／客户每天面对的选择。',
  'landlink.two.startHint': '按下开始，亲眼看着 AI 如何用旧习惯把五步浓缩成一个动作。',
  'landlink.two.startCta': '开始对比',
  'landlink.two.classicLabel': '传统做法',
  'landlink.two.classicStepsLabel': '5 个步骤',
  'landlink.two.step1': '开启订单系统',
  'landlink.two.step2': '逐项选择菜式',
  'landlink.two.step3': '输入数量与规格',
  'landlink.two.step4': '核对送货资料',
  'landlink.two.step5': '确认送出',
  'landlink.two.orderLabel': '示例订单',
  'landlink.two.orderItem': '🥬 白菜 × 2',
  'landlink.two.aiLabel': 'AI 做法',
  'landlink.two.aiStepsLabel': '1 个动作',
  'landlink.two.aiReadyLabel': 'AI 预先准备',
  'landlink.two.aiOrder': '🥬 白菜 × 2・送到中环店・今天上午',
  'landlink.two.evidenceIntro': '根据过往使用习惯',
  'landlink.two.ev1.label': '最近 4 次订单',
  'landlink.two.ev1.value': '全部是白菜 × 2',
  'landlink.two.ev2.label': '常用送货地点',
  'landlink.two.ev2.value': '中环店・9 次',
  'landlink.two.ev3.label': '星期一习惯',
  'landlink.two.ev3.value': '通常上午 10 时补货',
  'landlink.two.confirm': '照旧这张单・确认送出',
  'landlink.two.confirmed': '已确认送出',
  'landlink.two.aiNote': 'AI 只调用已授权的公司纪录，每个建议都有来源。',
  'landlink.three.titlePre': '不是叫人改变，',
  'landlink.three.titleMid': '是让改变',
  'landlink.three.titleHighlight': '值得',
  'landlink.three.titlePost': '。',
  'landlink.three.body': '动机、能力与提示缺一不可。落地通从管理与前线两端同时降低阻力、增加回报。',
  'landlink.three.cta': '开始对话',
  'landlink.three.card1.title': '管理层｜由上而下推动',
  'landlink.three.card1.copy': '让管理者看见采用进度，并拥有每天主动提醒的依据。',
  'landlink.three.card2.title': '前线层｜诱因落到个人',
  'landlink.three.card2.copy': '把新系统变成更快、更轻松，而且值得每天打开的选择。',
  'landlink.four.kicker': '让系统真正落地',
  'landlink.four.titlePre': '下一个被改变的，',
  'landlink.four.titleMid': '可以是',
  'landlink.four.titleHighlight': '你的系统',
  'landlink.four.titlePost': '。',
  'landlink.four.body': '告诉我们你正在推动的系统，我们会找出最值得先处理的一步。',
  'landlink.four.ctaLoading': '正在连接顾问…',
  'landlink.four.cta': 'WhatsApp 免费了解',
  'landlink.four.note': '无需电脑知识 · 专人为你讲解',
  'landlink.four.waPrefill': '您好，我想了解落地通 AI 习惯落地助手。',

  // Bizpulse（/bizpulse）
  'bizpulse.brandName': '商脉',
  'bizpulse.backAria': '返回首页',
  'bizpulse.title': '请一位永不偷懒的 AI 风控员',
  'bizpulse.kicker': 'AI 客户风险监察',
  'bizpulse.heroBody': '24 小时替你盯紧每位客户。',
  'bizpulse.heroStrong': '谁可能拖欠、流失、转投对手，AI 先一步通知你。',
  'bizpulse.heroCta': '看看 AI 如何替我盯客',
  'bizpulse.scanLabel': 'AI 正在替你巡查客户',
  'bizpulse.one.title': '风险未发生，AI 已看见。',
  'bizpulse.one.body': '按下 3 个红色警号，看看 AI 可以提早发现什么。',
  'bizpulse.one.closeAria': '关闭',
  'bizpulse.one.continueNext': '继续查看下一个警号 →',
  'bizpulse.one.continueDone': '查看 AI 风控员如何工作 →',
  'bizpulse.one.progressPre': '已查看',
  'bizpulse.one.progressPost': '个警号',
  'bizpulse.one.s1.title': '可能拖欠',
  'bizpulse.one.s1.tag': 'AI 发现：付款开始变慢',
  'bizpulse.one.s1.detail': '这位客户的付款日一再推迟。AI 会在变成坏账之前提醒你，让你及早跟进、保住现金流。',
  'bizpulse.one.s2.title': '可能流失',
  'bizpulse.one.s2.tag': 'AI 发现：客户正在冷却',
  'bizpulse.one.s2.detail': '查询少了、回复慢了、互动也降了。AI 每日替你监察，让你在客户离开前主动挽留。',
  'bizpulse.one.s3.title': '可能转投对手',
  'bizpulse.one.s3.tag': 'AI 发现：落单习惯改变',
  'bizpulse.one.s3.detail': '订单次数和数量开始反常。AI 会找出这些细微变化，提醒你把握最后的跟进机会。',
  'bizpulse.two.step1.title': '接驳纪录',
  'bizpulse.two.step1.copy': '集中客户资料',
  'bizpulse.two.step2.title': 'AI 每日巡查',
  'bizpulse.two.step2.copy': '找出反常行为',
  'bizpulse.two.step3.title': '即时报警',
  'bizpulse.two.step3.copy': '直接告知风险',
  'bizpulse.two.step4.title': '及早跟进',
  'bizpulse.two.step4.copy': '保住生意与现金',
  'bizpulse.two.finalSmall': '你的 AI 风控员',
  'bizpulse.two.titlePre': '不用学 AI。',
  'bizpulse.two.titleHighlight': 'AI 自动替你盯客。',
  'bizpulse.two.finalBody': '每日巡查、不会偷懒；一有风险，立即通知你。',
  'bizpulse.two.ctaLoading': '正在连接顾问…',
  'bizpulse.two.cta': 'WhatsApp 免费了解',
  'bizpulse.two.note': '无需电脑知识 · 专人为你讲解',
  'bizpulse.two.waPrefill': '您好，我想了解如何请一位 AI 风控员替我监察客户风险。',

  // Promo（/promo）
  'promo.kicker': '地产代理 × 社交媒体自动化',
  'promo.titlePre': '物业更新、发帖、追数据',
  'promo.titleHighlight': '一个系统，一次搞定。',
  'promo.heroLead': '新盘一入库，10 秒变成一则专业 FB 贴文——自动水印防盗、自动短链接追踪、自动统计哪个盘最抢手。',
  'promo.heroBold': '「照片转影片」',
  'promo.heroTail': '，用最小的精力，做到最佳的客户体验。',
  'promo.videoCaption': '▲ 实机演示：物业资料 → 一键生成专业影片（自动配乐、字幕、联络卡、QR）',
  'promo.ctaDemo': 'WhatsApp 立即预约 Demo',
  'promo.ctaTry': '线上亲自试玩',
  'promo.waPrefill': '我想了解物业社群同步方案',
  'promo.painTitle': '地产代理每天面对的三件事',
  'promo.pain1.t': '盘源日日变',
  'promo.pain1.d': '新盘、租出、售出——更新完网站，还要人手再发一次社交媒体，两边奔波',
  'promo.pain2.t': '相片一上网就被人盗',
  'promo.pain2.d': '辛苦拍摄的盘相，转头被人下载使用，连 AI 修图都防不住',
  'promo.pain3.t': '广告费花了如流水',
  'promo.pain3.d': '出了帖不知道哪个盘有人看、多少人点击，没数据等于没方向',
  'promo.stepsTitlePre': '由盘源到贴文，',
  'promo.stepsTitleHighlight': '三步、十秒',
  'promo.stepsNote': '以下为系统真实操作画面，不是概念图',
  'promo.screenshotAlt': '系统操作实景：社群推广区',
  'promo.screenshotCaption': '▲ 后台实景：勾选相片 → 文案自动生成（可复制编辑）→ 一键发布，贴文即时上 FB',
  'promo.step1.t': '选相',
  'promo.step1.d': '勾选物业相片，系统自动加上中央对角品牌水印——AI 修图也难以去除，无从盗用',
  'promo.step2.t': '发帖',
  'promo.step2.d': '贴文文案自动生成：标题、售租价、地址、短链接一键搞定，想改就按「复制文案」',
  'promo.step3.t': '看数',
  'promo.step3.d': '每则贴文自带追踪：FB 多少人点击、物业页多少人看，后台一目了然',
  'promo.magicBadge': '最强神器',
  'promo.magicTitlePre': '几张相，变一段',
  'promo.magicTitleHighlight': '专业物业影片。',
  'promo.magicBodyLead': '上载盘相，系统自动生成 1080p 影片：自动配乐、动态字幕（标题／价格／地址）、品牌标志、',
  'promo.magicBodyBold': '零剪片经验、零人手',
  'promo.magicBodyTail': '。影片贴文在 FB 的自然触及远高于照片贴文，同一份盘源，两种玩法。',
  'promo.bullet1': '照片轮播＋Ken Burns 动态效果',
  'promo.bullet2': '四种背景音乐风格，一键切换',
  'promo.bullet3': '方形 1:1／直式 9:16，FB IG 通用',
  'promo.compareTitle': '算一笔账给你听',
  'promo.compare.col1': '请人／人手处理',
  'promo.compare.col2': '这个系统',
  'promo.compare.r1.c0': '出一则贴文',
  'promo.compare.r1.c1': '30–60 分钟',
  'promo.compare.r1.c2': '10 秒',
  'promo.compare.r2.c0': '一段物业影片',
  'promo.compare.r2.c1': '半天起＋剪片费',
  'promo.compare.r2.c2': '1–2 分钟自动生成',
  'promo.compare.r3.c0': '相片防盗',
  'promo.compare.r3.c1': '无',
  'promo.compare.r3.c2': '内置 AI 级水印',
  'promo.compare.r4.c0': '每盘成效数据',
  'promo.compare.r4.c1': '无／另购工具',
  'promo.compare.r4.c2': '内置，后台直看',
  'promo.compare.r5.c0': '月费成本',
  'promo.compare.r5.c1': '$8,000–15,000',
  'promo.compare.r5.c2': '一个相宜月费',
  'promo.compareNote': '* 系统现正于物业代理实战运行中',
  'promo.ctaTitlePre': '下个新盘入库，',
  'promo.ctaTitleHighlight': '10 秒后已在 FB。',
  'promo.ctaSub': '约一节 15 分钟线上 Demo，用你自己的盘当场试一次。',
  'promo.ctaNote': '云端系统・资料自持・无长约・专人支援',
  'promo.footer': 'JKD Coding・以无法为有法',

  // ===== Distill 知识蒸馏所（/distill/）=====
  'distill.meta.title': '知识蒸馏所 — 把组织经验酿成会做事的规则｜JKD Coding',
  'distill.meta.description': 'JKD Coding 知识蒸馏所为大型企业与中小企提供知识萃取服务：把散落在会议、邮件与资深同事脑中的经验，蒸馏成可执行、可复用、可喂给 AI 员工的规则库。会议开完那一刻，规则已经上墙。',
  'distill.hero.kicker': 'Be Water, My Friend',
  'distill.hero.title': '知识如水，聚则成形',
  'distill.hero.tagline': '知识蒸馏所 · KNOWLEDGE DISTILLERY',
  'distill.hero.lead': 'JKD Coding 为大型企业与中小企提供知识萃取服务：把散落在会议、邮件与资深同事脑中的经验，蒸馏成可执行、可复用、可喂给 AI 员工的规则库——会议开完那一刻，规则已经上墙。',
  'distill.hero.cta1': '预约 30 分钟诊断',
  'distill.hero.cta2': '方法：蒸馏四步',
  'distill.pain.quote.en': 'Absorb what is useful, discard what is useless.',
  'distill.pain.quote.zh': '吸其有用，弃其无用——李小龙早已说出知识萃取的定义。',
  'distill.pain.heading': '知识蒸发的四种死法',
  'distill.pain.c1.tt': '老师傅退休',
  'distill.pain.c1.ds': '二十年的判断力，随一场欢送会归零——经验从未离开过他的脑。',
  'distill.pain.c2.tt': '会议记录坟场',
  'distill.pain.c2.ds': '存了，但没有人再看。第四千个档案躺在共享文件夹深处。',
  'distill.pain.c3.tt': 'SOP 石化',
  'distill.pain.c3.ds': '写完即过时——文件追不上真实做法，新人照着错的学。',
  'distill.pain.c4.tt': 'AI 空壳',
  'distill.pain.c4.ds': '没有喂入你们的知识，AI 只是一台礼貌的聊天机器人。',
  'distill.method.quote.en': "It's not the daily increase but daily decrease — hack away at the unessential.",
  'distill.method.quote.zh': '不是每日增加，而是每日递减——蒸馏是削法的艺术，规则墙是削出来的，不是囤出来的。',
  'distill.method.heading': '蒸馏循环四步',
  'distill.method.s1.tt': '拦截',
  'distill.method.s1.en': 'Intercept',
  'distill.method.s1.ds': '会议完结当日，记录直接进炉——不等它蒸发。',
  'distill.method.s1.out': '产出：原始知识入库',
  'distill.method.s2.tt': '蒸馏',
  'distill.method.s2.en': 'Distill',
  'distill.method.s2.ds': 'AI 加顾问双刀并落：AI 提炼，顾问削去非本质。',
  'distill.method.s2.out': '产出：候选规则',
  'distill.method.s3.tt': '沉淀',
  'distill.method.s3.en': 'Crystallize',
  'distill.method.s3.ds': '人工批核上墙，每条＝场景＋动作＋负责人。',
  'distill.method.s3.out': '产出：规则墙',
  'distill.method.s4.tt': '复用',
  'distill.method.s4.en': 'Reflow',
  'distill.method.s4.ds': 'AI 员工按规则行事，月度复检越用越准。',
  'distill.method.s4.out': '产出：组织记忆',
  'distill.show.kicker': 'The Distillery · 主秀',
  'distill.show.heading': '一滴知识的旅程',
  'distill.show.lead': '上层是每天落下来的原始噪音；中间那一道金线，是截住它的手；下层，是留下来会做事的规则。',
  'distill.show.ch1': '会议记录_v7_最终版（2）.docx',
  'distill.show.ch2': '第 38 次跨部门会议',
  'distill.show.ch3': '「他说改天再谈……」',
  'distill.show.ch4': '共享文件夹（4,200 个档案）',
  'distill.show.ch5': '老师傅下月退休',
  'distill.show.ch6': '邮件往来 217 封',
  'distill.show.funnel': '截 · 蒸 · 沉',
  'distill.show.rule1.tag': 'RULE · 会议',
  'distill.show.rule1.text': '每个会议必须以「谁＋做什么＋何时」收结，缺一即散会重议。',
  'distill.show.rule2.tag': 'RULE · 对外',
  'distill.show.rule2.text': '凡承诺类回复，先向内确认再发出——不替公司开口。',
  'distill.show.rule3.tag': 'RULE · 文件',
  'distill.show.rule3.text': '作业准则每 90 日复检一次，写的人签名，改的人留痕。',
  'distill.show.rule4.tag': 'RULE · 传承',
  'distill.show.rule4.text': '新人三日内读完规则墙，即可开工。',
  'distill.show.note': '样例规则为通用示意，非任何客户实际规则库内容',
  'distill.proof.kicker': 'Proof of Practice · 蒸馏者自我证明',
  'distill.proof.heading': '我们不是教你蒸馏——我们天天在蒸馏',
  'distill.proof.s1.n': '252',
  'distill.proof.s1.l': '条实战守则持续沉淀于自用治理系统',
  'distill.proof.s2.n': '每周',
  'distill.proof.s2.l': '收割复检节奏，从不间断',
  'distill.proof.s3.n': '六语',
  'distill.proof.s3.l': '全站知识治理经验，直接可移植',
  'distill.proof.quote.en': 'Using no way as way — having no limitation as limitation.',
  'distill.proof.quote.zh': '以无法为有法，以无限为有限——规则由实战沉淀而成，不是从范本抄袭而来。',
  'distill.fit.heading': '这个服务适合谁',
  'distill.fit.f1.tt': '非牟利组织',
  'distill.fit.f1.ds': '捐赠者关系与义工经验，不再随干事更替而流失。',
  'distill.fit.f2.tt': '社会企业',
  'distill.fit.f2.ds': '使命与营运经验传承，扩张时不变形。',
  'distill.fit.f3.tt': '中小企业',
  'distill.fit.f3.ds': '把老板脑中的判断，变成全公司可用的制度。',
  'distill.fit.f4.tt': '大型企业',
  'distill.fit.f4.ds': '跨部门知识治理：让每个部门的隐性经验，变成 AI 员工可执行的规则。',
  'distill.faq.q1': '知识蒸馏要多久？',
  'distill.faq.a1': '首轮蒸馏以「当日交付」为目标：会议完结当天，该场会议的规则已上墙。整套循环（拦截、蒸馏、沉淀、复用）按组织规模以周为单位推进，并以月度复检持续循环。',
  'distill.faq.q2': '我们的知识会外泄吗？',
  'distill.faq.a2': '不会。蒸馏产出的规则属于你，存放于你的管辖范围；我们只在服务期内经你授权处理，交付后退出。',
  'distill.faq.q3': '这跟一般 AI 知识管理工具有什么分别？',
  'distill.faq.a3': '工具只负责储存，蒸馏负责删减与成形。没有「谁＋做什么＋何时」的规则结构，资料再多也无法被执行——我们交付的是判断力，不是数据库。',
  'distill.faq.q4': '蒸馏后的规则如何接驳 AI 员工？',
  'distill.faq.a4': '规则墙是 AI 员工的工作依据：AI 项目经理按规则行事、按规则向内确认，月度复检时再把新经验回灌成新规则，形成闭环。',
  'distill.faq.q5': '大型企业与中小企的做法有什么不同？',
  'distill.faq.a5': '中小企聚焦把老板脑中的判断制度化；大型企业则处理跨部门的知识治理——每个部门各自的隐性经验，统一成互不冲突、可执行的规则集。',
  'distill.faq.q6': '如何开始？',
  'distill.faq.a6': '由一次 30 分钟诊断开始：盘点你组织中最值钱、也最危险（正在蒸发）的知识资产，再决定蒸馏范围与节奏。',
  'distill.funnel.heading': '由一滴噪音，到一队 AI 员工',
  'distill.funnel.f1.tag': 'STEP 1',
  'distill.funnel.f1.tt': '诊断',
  'distill.funnel.f1.ds': '30 分钟一对一，盘点你组织里最值钱、也最危险（正在蒸发）的知识资产。',
  'distill.funnel.f2.tag': 'STEP 2 · 本页',
  'distill.funnel.f2.tt': '知识蒸馏',
  'distill.funnel.f2.ds': '蒸馏循环四步，规则上墙，知识从此有了形状。',
  'distill.funnel.f3.tag': 'STEP 3',
  'distill.funnel.f3.tt': 'AI 员工代管',
  'distill.funnel.f3.ds': 'AI 项目经理驻场，按你的规则库开工——我们卖的是员工，不是软件。',
  'distill.cta1': 'WhatsApp 直谈',
  'distill.cta2': '了解诊断',
  'distill.wa.text': '你好！我想了解 JKD Coding 的知识蒸馏服务。',
  'distill.footer': 'JKD Coding · 以无法为有法',
  'distill.card.title': '知识萃取',
  'distill.card.desc': '把散落在会议与资深同事脑中的经验，蒸馏成可执行、可喂给 AI 员工的规则库。',
  'distill.card.cta': '进入知识蒸馏所',
  'distill.xs.title': '诊断之后：把发现蒸馏成规则',
  'distill.xs.body': '诊断找出瓶颈，知识蒸馏把组织经验变成可执行、可复用、可喂给 AI 员工的规则库——会议开完那一刻，规则已经上墙。',
  'distill.xs.cta': '进入知识蒸馏所',

  // ============ 条款及细则 /terms（三 TAB：隐私／退款／交付）============
  'terms.meta.title': '条款及细则 — 隐私・退款・交付政策｜JKD Coding',
  'terms.meta.description': 'JKD Coding（由 Wiseman Consultancy Limited 营运）之条款及细则：隐私政策、退款政策与交付政策，适用于本网站及所有顾问与数字开发服务。',
  'terms.title': '条款及细则',
  'terms.subtitle': '本条款适用于 JKD Coding 网站（jkdcoding.com）及我们提供的所有顾问与数字服务。网站及服务由 Wiseman Consultancy Limited 营运。',
  'terms.updated': '最后更新：2026-10-13',
  'terms.tab.privacy': '隐私政策',
  'terms.tab.refund': '退款政策',
  'terms.tab.delivery': '交付政策',
  'terms.contact': '如有疑问，欢迎透过 WhatsApp +852 5165 1480 与我们联络。',

  // — 隐私政策 —
  'terms.privacy.note': '本政策说明 JKD Coding（由 **Wiseman Consultancy Limited** 营运）如何收集、使用及保障你的资料。使用本网站或服务即表示你同意本政策的条款。',
  'terms.privacy.h1': '一、我们收集的资料',
  'terms.privacy.s1': '- **查询与联络资料**：姓名、WhatsApp 号码、邮箱、机构名称（经诊断预约、WhatsApp 或表单提供）\n- **项目资料**：你提供的业务资料、知识库内容、网站内容与素材\n- **浏览资料**：页面浏览、来源页与营销参数（匿名统计，用于改善网站与广告效果）\n- **付款资料**：我们只保留发票编号与金额，**不会接触或储存你的支付工具资料**',
  'terms.privacy.h2': '二、资料用途',
  'terms.privacy.s2': '- 提供、营运及改善我们的顾问与开发服务\n- 报价、项目管理、技术支持与账务处理\n- 重要服务通知（预约确认、交付安排、安全警示）',
  'terms.privacy.h3': '三、第三方处理（AI 引擎）',
  'terms.privacy.s3': '为提供 AI 相关服务，客户提供的内容可能传送至我们采用的大型语言模型服务商（包括 DeepSeek、Z.ai 等）处理。该等服务商按其自身隐私政策处理资料；我们只传送完成工作所需的内容，并持续审视其资料保障安排。',
  'terms.privacy.h4': '四、资料保留',
  'terms.privacy.s4': '- 查询与对话纪录：保留约 24 个月后清理\n- 项目档案：交付后保留 90 日供修复与支持，其后应要求删除或自动清理',
  'terms.privacy.h5': '五、我们不会做的事',
  'terms.privacy.s5': '- 不出售、不租借你的任何资料\n- 不将你或你客户的资料用于第三方推销\n- 除法律要求或提供服务所必需（如 AI 引擎、托管与支付服务商）外，不向第三方披露',
  'terms.privacy.h6': '六、你的权利',
  'terms.privacy.s6': '你有权查阅、更正或删除你的个人资料。透过 WhatsApp +852 5165 1480 提出要求，我们将于 **14 日内**回应。我们的处理方式符合《个人资料（私隐）条例》（香港法例第 486 章）。',
  'terms.privacy.h7': '七、政策更新',
  'terms.privacy.s7': '如有重大变更，我们会于本页公布并更新「最后更新」日期。重大影响服务使用的变更会另行通知。',

  // — 退款政策 —
  'terms.refund.note': 'JKD Coding 提供的是数字顾问与开发服务（非实体货品），所有价格以港币计算。我们鼓励你先预约**免费诊断**（30 分钟一对一），了解清楚再决定付费。',
  'terms.refund.h1': '一、免费诊断',
  'terms.refund.s1': '诊断完全不收费，亦无销售压力；所有收费项目均在诊断后报价，经你书面确认方才开始收费。',
  'terms.refund.h2': '二、开工前取消',
  'terms.refund.s2': '项目确认后、尚未开工前取消：已缴款项**全数退回**；如已为你产生第三方成本（域名、授权、云端资源等），凭单据实报实销扣除。',
  'terms.refund.h3': '三、现成方案与元件库',
  'terms.refund.s3': '付款后 **7 日内**，如服务因我们的原因未能交付或无法正常运作，且我们无法在合理时间内修复，你可申请**全额退款**。已成功交付并正常运作者，视为已完成，不设退款。',
  'terms.refund.h4': '四、订制项目（里程碑制）',
  'terms.refund.s4': '- 订制项目按里程碑收费：**已开工的里程碑**按实际完成进度结算，**未开工的里程碑**可退\n- 中途取消：已完成并通过验收的工作不设退款，未完成部分按比例退还\n- 交付后的修改需求属新增范围，另行报价',
  'terms.refund.h5': '五、错误扣款',
  'terms.refund.s5': '如发现重复扣款或金额错误，经核实后将于 **7 个工作日内**全额原路退回。',
  'terms.refund.h6': '六、申请方式',
  'terms.refund.s6': 'WhatsApp +852 5165 1480（请提供发票编号）。所有申请于 **7 个工作日内**处理，款项经原支付渠道退回。',

  // — 交付政策 —
  'terms.delivery.note': 'JKD Coding 提供的是纯数字服务——**无实体货运**，所有交付均以电子方式进行，不产生运费。',
  'terms.delivery.h1': '一、交付时限',
  'terms.delivery.s1': '- **现成方案**：确认需求及付款后最快 30 分钟完成部署（办公时间内）\n- **元件库**：授权与文件于付款后 1 个工作日内以邮箱／WhatsApp 发送\n- **订制项目**：按合约里程碑交付，一般 6–8 周上线\n- 非办公时间的委托将于下一个办公时段处理；所有查询 24 小时内回复',
  'terms.delivery.h2': '二、交付内容',
  'terms.delivery.s2': '按方案而定，一般包括：网站／系统的访问权限、源代码或部署环境、设置与使用指引、技术支持联络渠道。',
  'terms.delivery.h3': '三、交付方式',
  'terms.delivery.s3': '全部以电子方式交付（网页、邮箱、WhatsApp）。完成部署并通过双方验收后，服务即视为已送达并开始提供。',
  'terms.delivery.h4': '四、交付延误',
  'terms.delivery.s4': '如超过承诺时限仍未交付，你可选择**尽快补交**，或按[退款政策](#refund)申请退款。',

  // — footer 条款链接 —
  'footer.termsLink': '条款及细则',
};

export default dict;
